import type { TLRichText, TLShape } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { Point } from "../shared/schema";
import {
  boundsForPoints,
  commentRegionPoints,
  type PendingRegionComment,
} from "./comment-regions";

/*
 * Cmd/Ctrl+K: a new comment pinned to the selection with @Agent already in
 * it. Posting it is the same @Agent call as typing it, so the pin decides
 * what the agent works on.
 */
export type AskAgentPending =
  | { kind: "point"; shape: TLShape | null; p: Point }
  | PendingRegionComment;

/** One object: a pin on it. Several: a region around them. None: the centre of the view. */
export function askAgentPending(editor: Editor): AskAgentPending {
  const shapes = editor.getSelectedShapes();
  if (shapes.length === 1)
    return {
      kind: "point",
      shape: shapes[0],
      p: editor.getShapeGeometry(shapes[0]).bounds.center,
    };
  if (!shapes.length)
    return { kind: "point", shape: null, p: editor.getViewportPageBounds().center };
  // Shapes that share a parent shape (usually a sheet) keep the region on it,
  // so the mark moves with the sheet; otherwise it stays in page space.
  const parents = new Set(shapes.map((s) => s.parentId));
  const parent =
    parents.size === 1
      ? editor.getShape([...parents][0] as TLShape["id"]) ?? null
      : null;
  const corners = shapes.flatMap((s) => {
    const transform = editor.getShapePageTransform(s);
    if (!transform) return [];
    return commentRegionPoints({
      ...editor.getShapeGeometry(s).bounds,
      pinX: 0,
      pinY: 0,
    }).map((p) => transform.applyToPoint(p));
  });
  const local = parent
    ? corners.map((p) => editor.getPointInShapeSpace(parent, p))
    : corners;
  return {
    kind: "region",
    target: parent,
    region: { ...boundsForPoints(local), pinX: 1, pinY: 0 },
    stroke: [],
  };
}

/** A comment body that is just @Agent and a space, ready for the request. */
export const agentRequest = (): TLRichText => ({
  type: "doc",
  content: [
    {
      type: "paragraph",
      content: [
        { type: "agentMention", attrs: { occurrenceId: crypto.randomUUID() } },
        { type: "text", text: " " },
      ],
    },
  ],
});

/** How the shortcut is written on this platform. */
export const askAgentShortcut = () =>
  /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K";
